import { MenuItemType, PlatformType } from "../../../../types/baseTypes";
import styles from "./SubMenu.module.scss";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import React, { useEffect, useRef, useState } from "react";
import { useAppContext } from "../../../../context/AppContext";
import MenuItem from "../MenuItem/MenuItem";
import { MenuParentType } from "../Menu";
import { isMenuOrChildMenuSelected } from "../utils";
import { FocusTrap } from "@aleksundshantu/aunds-utils-react/accessibility";
import { useKeyboardNavigation } from "../keyboard-navigation";
import { Post } from "@/wp/types/client";
import {
  OptionsPromoBoxConfigType,
  OptionsPromoBoxItemType,
} from "@/types/OptionType";
import { NavbarPromoBox } from "../../NavbarPromoBox/NavbarPromoBox";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG";

interface SubMenuItemProps {
  item: MenuItemType;
  pageData?: Post;
  currentSubmenu: MenuParentType;
  parentSubMenu: MenuParentType;
  subMenu: MenuParentType;
  parentMenuItem: MenuItemType;
  setCurrentSubmenu: (value: MenuParentType) => void;
  controlParentMenu: (action: "close" | "prev" | "next") => void;
  promoBoxOption: OptionsPromoBoxConfigType;
  socialMedia: PlatformType;
  isNobleElements?: boolean;
}

const SubMenu = ({
  item,
  pageData,
  currentSubmenu,
  setCurrentSubmenu,
  parentSubMenu,
  subMenu,
  parentMenuItem,
  controlParentMenu,
  promoBoxOption,
  socialMedia,
  isNobleElements,
}: SubMenuItemProps) => {
  const { navbarSize } = useAppContext();
  const { getIcon } = useGlobalContext();

  const ref = useRef<HTMLDivElement | null>(null);
  const promoboxRef = useRef<HTMLElement | null>(null);
  const [outOfViewport, setOutOfViewport] = useState(false);

  const promobox =
    promoBoxOption?.show_promobox &&
    promoBoxOption?.promoboxes &&
    (promoBoxOption.promoboxes as OptionsPromoBoxItemType[]).find((box) =>
      box.menu_item?.includes(item.ID?.toString() || ""),
    );

  const { items, keyDown, controlMenu, firstItemFocused } =
    useKeyboardNavigation({
      menu: item,
      setCurrentSubmenu,
      type: "submenu",
      controlParentMenu,
      subMenu,
    });

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // true when element not fully inside
        setOutOfViewport(entry.intersectionRatio < 1);
      },
      {
        threshold: 0,
      },
    );

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, []);

  const TwoColumn =
    Array.isArray(item.child_items) && item.child_items.length > 4;

  const ULJSX = (
    <ul
      style={{ "--submenu-columns": TwoColumn ? 2 : 1 } as React.CSSProperties}
      className={styles["navbar-submenu-list"]}
      role="menu"
      onKeyDown={keyDown}
    >
      {Array.isArray(item.child_items) &&
        item.child_items.map((child, key) => {
          if (!items.current[key]) {
            items.current[key] = React.createRef<HTMLLIElement>();
          }
          return (
            <MenuItem
              item={child}
              key={key}
              pageData={pageData}
              currentSubmenu={currentSubmenu}
              setCurrentSubmenu={setCurrentSubmenu}
              parentSubMenu={item}
              subMenu={item}
              tabIndex={-1}
              isHome={false}
              itemRef={items.current[key]}
              controlParentMenu={controlMenu}
              onFocus={key == 0 ? firstItemFocused : undefined}
              promoBoxOption={promoBoxOption}
              socialMedia={socialMedia}
            />
          );
        })}
    </ul>
  );

  return (
    <FocusTrap
      innerRef={ref}
      dep={currentSubmenu}
      disabled={currentSubmenu?.ID !== item.ID}
    >
      <div
        className={`${styles["navbar-submenu"]} ${outOfViewport ? styles["out-of-viewport"] : ""} ${promobox ? styles["has-promobox"] : ""} navbar-submenu ${currentSubmenu?.ID !== item.ID ? "trap enabled" : "trap disabled"} ${isMenuOrChildMenuSelected(item, currentSubmenu) ? styles["is-open"] : ""}`}
        style={
          navbarSize?.width
            ? ({
                "--submenu-width": `${navbarSize.width}px`,
              } as React.CSSProperties)
            : {}
        }
      >
        <button
          onClick={() => {
            setCurrentSubmenu(parentSubMenu);
          }}
          className={`${styles["sub-menu-back"]} back button-reset is-flex is-align-items-center is-gap-2 is-hidden-after-navbar-breakpoint has-text-centered`}
        >
          <CustomSVG src={getIcon("arrowLeft")} />
          {parentMenuItem.title}
        </button>

        <div className={styles["submenu-content"]}>
          <div
            className={`${!promobox ? "mb-6" : ""} ${styles["submenu-items-col"]}`}
          >
            {ULJSX}
          </div>
          {promobox && (
            <NavbarPromoBox
              data={promobox}
              ref={promoboxRef}
              extraClass="is-hidden-after-navbar-breakpoint mb-6"
              isNobleElements={isNobleElements}
            />
          )}
        </div>
        {/* 
        <div className="is-hidden-after-navbar-breakpoint" role="none">
          <Button
            type="primary"
            href="#"
            target="_self"
            className="back button-reset is-flex is-align-items-center is-gap-2"
            role="menuitem"
            onClick={() => {
              setCurrentSubmenu(parentSubMenu);
            }}
          >
            <span>{getLabel("back")}</span>
          </Button>
        </div> */}
        {promobox && (
          <NavbarPromoBox
            data={promobox}
            ref={null}
            extraClass="is-hidden-before-navbar-breakpoint"
            isNobleElements={isNobleElements}
          />
        )}
      </div>
    </FocusTrap>
  );
};

export default SubMenu;
